import { pxToRem } from '@fluentsms/agentnet-web-components';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Close, FileCopy, UnfoldLess, UnfoldMore } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import { FC, useCallback, useMemo, useState } from 'react';
import JsonTreeView from './JsonTreeView';

interface IdvDetailedJsonDialogProps {
  open: boolean;
  onClose: () => void;
  jsonData: unknown;
  loading: boolean;
  error: string | null;
  customerName?: string;
}

interface ExtractedImage {
  label: string;
  base64: string;
}

interface ImagePathConfig {
  path: string[];
  label: string;
}

const IMAGE_SUFFIXES: { path: string[]; label: string }[] = [
  { path: ['images', 'front'], label: 'Front of ID' },
  { path: ['images', 'back'], label: 'Back of ID' },
  { path: ['images', 'selfie'], label: 'Selfie' },
  { path: ['images', 'face'], label: 'Face from ID' },
  { path: ['facial', 'data', 'photoFace'], label: 'Face Photo' },
  { path: ['OCR', 'data', 'fullDocumentImageBase64'], label: 'Document Image' },
  { path: ['OCR', 'data', 'faceImageBase64'], label: 'Face from Document' },
];

const IMAGE_PARENT_KEYS = ['Detail', 'ReportData'];

const IMAGE_PATHS: ImagePathConfig[] = IMAGE_PARENT_KEYS.flatMap((parent) =>
  IMAGE_SUFFIXES.map(({ path, label }) => ({ path: [parent, ...path], label })),
);

const IMAGE_OMITTED_PLACEHOLDER = '[Image data omitted]';

function getNestedValue(obj: Record<string, unknown>, path: string[]): unknown {
  let current: unknown = obj;
  for (const key of path) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function setNestedValue(obj: Record<string, unknown>, path: string[], value: unknown): void {
  let current: unknown = obj;
  for (let i = 0; i < path.length - 1; i++) {
    if (current == null || typeof current !== 'object') return;
    current = (current as Record<string, unknown>)[path[i]];
  }
  if (current != null && typeof current === 'object') {
    (current as Record<string, unknown>)[path[path.length - 1]] = value;
  }
}

function unwrapResultItems(data: unknown): Record<string, unknown>[] {
  if (data == null || typeof data !== 'object') return [];

  const record = data as Record<string, unknown>;
  const items: Record<string, unknown>[] = [];

  if (Array.isArray(record['Result'])) {
    items.push(...(record['Result'] as Record<string, unknown>[]));
  } else if (record['Result'] != null && typeof record['Result'] === 'object') {
    items.push(record['Result'] as Record<string, unknown>);
  } else if (Array.isArray(record)) {
    items.push(...(record as unknown as Record<string, unknown>[]));
  } else {
    items.push(record);
  }

  return items;
}

function extractImages(data: unknown): ExtractedImage[] {
  if (data == null || typeof data !== 'object') return [];

  const images: ExtractedImage[] = [];
  const items = unwrapResultItems(data);

  for (const item of items) {
    for (const config of IMAGE_PATHS) {
      const value = getNestedValue(item, config.path);
      if (typeof value === 'string' && value.length > 100) {
        images.push({ label: config.label, base64: value });
      }
    }
  }

  return images;
}

function sanitizeJsonData(data: unknown): unknown {
  if (data == null || typeof data !== 'object') return data;

  const clone = JSON.parse(JSON.stringify(data));
  const items = unwrapResultItems(clone);

  for (const item of items) {
    for (const config of IMAGE_PATHS) {
      const value = getNestedValue(item, config.path);
      if (typeof value === 'string' && value.length > 100) {
        setNestedValue(item, config.path, IMAGE_OMITTED_PLACEHOLDER);
      }
    }
  }

  return clone;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    maxWidth: 960,
    minWidth: '50vw',
    height: '80vh',
    width: '100%',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dialogContent: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: 0,
    overflow: 'auto',
  },
  imageGallery: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    overflowX: 'auto',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  imageCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
  },
  image: {
    maxHeight: 200,
    maxWidth: 280,
    objectFit: 'contain',
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  imageLabel: {
    marginTop: theme.spacing(0.5),
    fontSize: pxToRem(12),
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontWeight: 500,
  },
  jsonTreeContainer: {
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: pxToRem(12),
    lineHeight: 1.8,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    minHeight: '100%',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(4),
  },
  errorContainer: {
    padding: theme.spacing(2),
  },
  actionButton: {
    color: theme.palette.primary.main,
  },
}));

const IdvDetailedJsonDialog: FC<IdvDetailedJsonDialogProps> = ({
  open,
  onClose,
  jsonData,
  loading,
  error,
  customerName,
}) => {
  const classes = useStyles();
  const [copySuccess, setCopySuccess] = useState(false);
  const [expandVersion, setExpandVersion] = useState(0);

  const images = useMemo(() => extractImages(jsonData), [jsonData]);
  const sanitizedData = useMemo(() => sanitizeJsonData(jsonData), [jsonData]);
  const formattedJson = sanitizedData ? JSON.stringify(sanitizedData, null, 2) : '';

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(formattedJson);
      setCopySuccess(true);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = formattedJson;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
    }
  }, [formattedJson]);

  const handleExpandAll = useCallback(() => {
    setExpandVersion((v) => Math.abs(v) + 1);
  }, []);

  const handleCollapseAll = useCallback(() => {
    setExpandVersion((v) => -(Math.abs(v) + 1));
  }, []);

  const title = customerName ? `JSON Response - ${customerName}` : 'JSON Response';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="idv-detailed-json-dialog-title"
        maxWidth={false}
        PaperProps={{ className: classes.dialogPaper }}
        data-testid="idv-detailed-json-dialog"
      >
        <DialogTitle id="idv-detailed-json-dialog-title" disableTypography>
          <div className={classes.dialogTitle}>
            <Typography variant="h3" color="textPrimary">
              {title}
            </Typography>
            <div className={classes.titleActions}>
              {jsonData && !loading && !error && (
                <>
                  <IconButton
                    className={classes.actionButton}
                    onClick={handleExpandAll}
                    aria-label="Expand all"
                    title="Expand all"
                    data-testid="idv-json-expand-all"
                  >
                    <UnfoldMore fontSize="small" />
                  </IconButton>
                  <IconButton
                    className={classes.actionButton}
                    onClick={handleCollapseAll}
                    aria-label="Collapse all"
                    title="Collapse all"
                    data-testid="idv-json-collapse-all"
                  >
                    <UnfoldLess fontSize="small" />
                  </IconButton>
                  <IconButton
                    className={classes.actionButton}
                    onClick={handleCopy}
                    aria-label="Copy JSON to clipboard"
                    data-testid="idv-json-copy-button"
                  >
                    <FileCopy fontSize="small" />
                  </IconButton>
                </>
              )}
              <IconButton onClick={onClose} aria-label="Close" data-testid="idv-json-close-button">
                <Close fontSize="small" />
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {loading && (
            <Box className={classes.loadingContainer} data-testid="idv-json-loading">
              <CircularProgress />
            </Box>
          )}
          {error && !loading && (
            <Box className={classes.errorContainer}>
              <Alert severity="error" data-testid="idv-json-error">
                {error}
              </Alert>
            </Box>
          )}
          {jsonData && !loading && !error && (
            <>
              {images.length > 0 && (
                <Box className={classes.imageGallery} data-testid="idv-json-image-gallery">
                  {images.map((img, idx) => (
                    <Box key={`${img.label}-${idx}`} className={classes.imageCard}>
                      <img src={`data:image/jpeg;base64,${img.base64}`} alt={img.label} className={classes.image} />
                      <Typography className={classes.imageLabel}>{img.label}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <div className={classes.jsonTreeContainer} data-testid="idv-json-content">
                <JsonTreeView data={sanitizedData} expandVersion={expandVersion} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopySuccess(false)} severity="success">
          JSON copied to clipboard
        </Alert>
      </Snackbar>
    </>
  );
};

export default IdvDetailedJsonDialog;
